import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft, Upload, FileText, Download, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { AiWorkspace } from '../components/ai/AiWorkspace';

const CaseDetailPage = () => {
  const { caseId } = useParams();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [evidences, setEvidences] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchCaseData();
    fetchEvidences();
    fetchActivities();
  }, [caseId]);

  const fetchCaseData = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}`, { withCredentials: true });
      setCaseData(data);
    } catch (error) {
      toast.error('Failed to load case');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvidences = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/evidence`, { withCredentials: true });
      setEvidences(data);
    } catch (error) {
      console.error('Failed to load evidences');
    }
  };

  const fetchActivities = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/activity`, { withCredentials: true });
      setActivities(data);
    } catch (error) {
      console.error('Failed to load activities');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/evidence`, formData, { withCredentials: true });
      toast.success('Evidence uploaded');
      fetchEvidences();
      fetchActivities();
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const exportReport = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/report`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `case_report_${caseId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report downloaded');
      fetchActivities();
    } catch (error) {
      toast.error('Export failed');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <Link to="/dashboard" className="text-gray-600 hover:text-gray-900"><ArrowLeft className="w-6 h-6" /></Link>
            <Shield className="w-8 h-8 text-blue-600" />
            <span className="text-2xl font-bold text-gray-900">InvestiGate</span>
            <div className="flex-1"></div>
            <button onClick={exportReport} data-testid="export-pdf-btn" className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition">
              <Download className="w-4 h-4" />
              <span>Export PDF</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <h1 className="text-3xl font-bold text-gray-900">{caseData?.title}</h1>
            <span className={`px-3 py-1 rounded text-sm font-semibold ${caseData?.status === 'open' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
              {caseData?.status}
            </span>
          </div>
          <p className="text-gray-600 mb-4">{caseData?.description}</p>
          <div className="flex space-x-4 text-sm">
            <span className="text-gray-500">Type: <span className="font-semibold">{caseData?.case_type}</span></span>
            <span className="text-gray-500">Priority: <span className="font-semibold">{caseData?.priority}</span></span>
          </div>
        </div>

        <div className="mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Evidence</h2>
              <label className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition cursor-pointer flex items-center space-x-2">
                <Upload className="w-4 h-4" />
                <span>{uploading ? 'Uploading...' : 'Upload'}</span>
                <input type="file" data-testid="evidence-upload-input" onChange={handleFileUpload} className="hidden" disabled={uploading} />
              </label>
            </div>
            <div className="space-y-2">
              {evidences.length === 0 ? (
                <p className="text-gray-500 text-sm">No evidence uploaded yet</p>
              ) : (
                evidences.map((ev) => (
                  <div key={ev.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <FileText className="w-5 h-5 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">{ev.filename}</span>
                    </div>
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/evidence/${ev.id}/download`} target="_blank" rel="noopener noreferrer" data-testid={`evidence-download-${ev.id}`} className="text-blue-600 text-sm hover:underline">Download</a>
                  </div>
                ))
              )}
            </div>
          </div>

        </div>

        <AiWorkspace caseId={caseId} evidences={evidences} onActivity={fetchActivities} />

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Clock className="w-5 h-5 text-gray-600" />
            <h2 className="text-xl font-bold text-gray-900">Activity Log</h2>
          </div>
          <div className="space-y-3">
            {activities.length === 0 ? (
              <p className="text-gray-500 text-sm">No activities recorded yet</p>
            ) : (
              activities.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-semibold text-sm text-gray-900">{activity.user_name}</span>
                      <span className="text-xs text-gray-500">{new Date(activity.timestamp).toLocaleString()}</span>
                    </div>
                    <p className="text-sm text-gray-700"><span className="font-medium">{activity.action.replace(/_/g, ' ')}</span> - {activity.details}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default CaseDetailPage;
